import { resolveComplexTemplate } from "../../utils/template/resolveComplexTemplate.js";
import { enhanceHTML } from "../../utils/template/enhanceHTML.js";
import * as templateFunctions from "../../map/popup/templateFunctions/index.js";
import { TabGroup } from "../TabGroup.js";

/**
 * Class to manage the media caption in the media lightbox
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class MediaCaption {
    /**
    * Format the caption in a table based on a metadata template
    * @param {object} featureCollection
    * @param {Number} index 
    * @param {object} metadataTemplate 
    * @private
    */
    static getCaption(featureCollection, index, metadataTemplate) {

        const feature = featureCollection.features[index];

        const createCaption = (content) => {
            const data = Object.assign({ response: featureCollection }, content)
            const caption = resolveComplexTemplate(metadataTemplate, data, templateFunctions, {
                verbose: false,
                returnAsString: false,
                postProcess: true,
                markdown: true
            });
            enhanceHTML(caption);
            return caption;
        }

        if (!Array.isArray(feature))
            return createCaption(feature);

        const tabGroup = new TabGroup();
        for (let i = 0; i < feature.length; ++i) {
            const caption = createCaption(feature[i]);
            tabGroup.addGroup(i + 1, caption);
        }
        return tabGroup.getElement();
    }
}

/**
 * Class to manage the visibility of elements via adding and removing HTML class attributes. The latter are then picked up in css styles
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class ContentVisibility {
    static setStateClass(target, isContentVisible, contentHiddenClassName, contentVisibleClassName) {
        if (target) {
            if (isContentVisible) {
                target.classList.remove(contentHiddenClassName);
                target.classList.add(contentVisibleClassName);
            }
            else {
                target.classList.remove(contentVisibleClassName);
                target.classList.add(contentHiddenClassName);
            }
        }
    }
}

/**
 * Class to manage button elements in the UI of the media lightbox
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class DataButton {
    constructor(target, params = {}) {
        this.target = target;
        const defaultParams = {
            'isContentVisible': false,
            'contentVisibleClassName': 'opened',
            'contentHiddenClassName': 'closed',
            'contentBoxElement': undefined,
            'buttonHideContent': 'fas fa-arrow-left',
            'buttonShowContent': 'fas fa-bars',
            'buttonHideContentText': 'Close',
            'buttonShowContentText': 'Open',
            'buttonClassNames': ['header-item-button', 'data-button']
        }
        Object.assign(this, defaultParams, params);
        this._initButton();
    }

    _initButton() {
        const dataButton = document.createElement('span');
        dataButton.classList.add(...this.buttonClassNames);
        dataButton.innerHTML = '<i></i>';
        this.target.append(dataButton);
        this.dataButton = dataButton;
        this._changeButton();
        this._addButtonFunctionality();
    }

    _changeButton() {
        const dataButtonIcon = this.dataButton.querySelector('i');
        if (this.isContentVisible) {
            dataButtonIcon.className = this.buttonHideContent;
            this.dataButton.title = this.buttonHideContentText;
        }
        else {
            dataButtonIcon.className = this.buttonShowContent;
            this.dataButton.title = this.buttonShowContentText;
        }
    }

    _addButtonFunctionality() {
        this.dataButton.addEventListener('click', () => {
            this.toggleContent();
        })
    }

    toggleContent() {
        if (this.isContentVisible)
            this.closeContent()
        else
            this.openContent()
    }

    openContent() {
        this.isContentVisible = true;
        ContentVisibility.setStateClass(this.contentBoxElement, this.isContentVisible, this.contentHiddenClassName, this.contentVisibleClassName);
        this._changeButton();
    }

    closeContent() {
        this.isContentVisible = false;
        ContentVisibility.setStateClass(this.contentBoxElement, this.isContentVisible, this.contentHiddenClassName, this.contentVisibleClassName);
        this._changeButton();
    }

    initContentBox(contentBoxElement) {
        this.contentBoxElement = contentBoxElement;
        ContentVisibility.setStateClass(this.contentBoxElement, this.isContentVisible, this.contentHiddenClassName, this.contentVisibleClassName);
    }
}

/**
 * Close button class for the UI of the media lightbox
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @extends DataButton
 * @private
 */
export class CloseButton extends DataButton {
    constructor(target) {
        const params = {
            'isContentVisible': null,
            'contentVisibleClassName': null,
            'contentHiddenClassName': null,
            'contentBoxElement': null,
            'buttonHideContent': 'fas fa-times fa-lg',
            'buttonShowContent': 'fas fa-times fa-lg',
            'buttonHideContentText': 'Close button error',
            'buttonShowContentText': 'Close',
            'buttonClassNames': ['header-item-button', 'close-button']
        }
        super(target, params)
    }

    onClick(callback) {
        this.dataButton.addEventListener('click', callback);
    }
}

/**
 * Metadata button class for the UI of the media lightbox
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @extends DataButton
 * @private
 */
export class MetadataButton extends DataButton {

    constructor(target) {
        const params = {
            'isContentVisible': false,
            'contentVisibleClassName': 'opened',
            'contentHiddenClassName': 'closed',
            'buttonHideContent': 'fas fa-arrow-left',
            'buttonShowContent': 'fas fa-bars',
            'buttonHideContentText': 'Close',
            'buttonShowContentText': 'Open',
            'buttonClassNames': ['header-item-button', 'metadata-button']
        }
        super(target, params)
    }

    setContent(contentNode, targetContent) {
        if (!this.contentBoxElement) {
            const contentBox = document.createElement('div');
            contentBox.classList.add('metadata-section');
            contentBox.append(contentNode);
            targetContent.append(contentBox);
            this.initContentBox(contentBox);
        }
        else {
            this.contentBoxElement.childNodes[0].replaceWith(contentNode)
        }
    }
}


/**
 * Share button class for the UI of the media lightbox
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @extends DataButton
 * @private
 */
export class ShareButton extends DataButton {
    constructor(target) {
        const params = {
            'isContentVisible': false,
            'contentVisibleClassName': 'opened',
            'contentHiddenClassName': 'closed',
            'buttonHideContent': 'fas fa-share-square',
            'buttonShowContent': 'fas fa-share-square',
            'buttonHideContentText': 'Toggle Share Box',
            'buttonShowContentText': 'Share',
            'buttonClassNames': ['header-item-button', 'share-button']
        }

        super(target, params);

    }

    setLink(link) {
        this.link = link;
        this.contentBoxElement.querySelector('input#share-link-input').value = this.link;
    }

    createBox(target) {

        const contentBox = document.createElement('div');
        contentBox.className = "share-box"
        contentBox.innerHTML = `
            <form>
            <fieldset>
                <label class="header" for="share-link-input">Share Item in Gallery</label>
                <div>
                    <input id="share-link-input" type="text" value="placeholder" readonly>
                    <button class="copy-button" title="Copy" type="button" resolved>  <i class="far fa-copy"></i> </button>
                </div>
                <div class="copy-message">
                    Copied Link
                </div>
                <div>
                    <button class="close-button" type="button" resolved> Close </button>
                </div>
            </fieldset>
        </form>
        `;
        target.append(contentBox);

        this.initContentBox(contentBox)

        target.querySelector('.share-box .copy-button').addEventListener('click', function () {
            navigator.clipboard.writeText(this.link);
            const messageElement = target.querySelector('.share-box .copy-message');
            if (!messageElement.classList.contains('visible')) {
                messageElement.classList.remove('hidden');
                messageElement.classList.add('visible');
                window.setTimeout(function () {
                    messageElement.classList.remove('visible');
                    messageElement.classList.add('hidden')
                }.bind(this), 5000);
            }
        }.bind(this));

        target.querySelector('.share-box .close-button').addEventListener('click', function () {
            this.closeContent();
        }.bind(this))
    }
}

/**
 * Text class for the UI of the media lightbox
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class DataText {

    constructor(target, params = {}) {
        const defaultParams = {
            'isContentVisible': false,
            'contentVisibleClassName': 'visible',
            'contentHiddenClassName': 'hidden',
            'textClassNames': ['header-item-text', 'data-text']
        }
        Object.assign(this, defaultParams, params);
        this.target = target;
        this._initText();
    }

    _initText() {
        const text = document.createElement('div');
        text.classList.add(...this.textClassNames);
        this.target.append(text);
        this.textElement = text;
    }

    setVisibility() {
        ContentVisibility.setStateClass(this.textElement, this.isContentVisible, this.contentHiddenClassName, this.contentVisibleClassName);
    }
}

/**
 * Zoom text class for the UI of the media lightbox
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @extends DataText
 * @private
 */
export class ZoomText extends DataText {

    constructor(target) {
        const params = {
            'isContentVisible': true,
            'contentVisibleClassName': 'visible',
            'contentHiddenClassName': 'hidden',
            'textClassNames': ['header-item-text', 'zoom-text']
        }
        super(target, params);
    }

    setZoom(zoomLevel) {
        this.setVisibility();
        const zoomValue = Math.round(zoomLevel * 100) + '%';
        this.textElement.innerHTML = 'Zoom: ' + zoomValue;
    }
}